/**
 * Расширенный менеджер middleware для CSElement
 * Поддерживает приоритеты, модификацию операций, статистику и таймауты
 */

import { EventEmitter } from 'eventemitter3';
import {
  MiddlewareConfig,
  MiddlewareContext,
  MiddlewareResult,
  MiddlewareStats,
  MiddlewarePriority,
  AdvancedMiddlewareManager
} from '../types/plugin-interfaces';
import { generateId } from '../utils/helpers';

interface InternalMiddlewareConfig extends MiddlewareConfig {
  id: string;
  enabled: boolean;
  stats: MiddlewareStats;
}

export class AdvancedMiddlewareManagerImpl extends EventEmitter implements AdvancedMiddlewareManager {
  private middleware: Map<string, InternalMiddlewareConfig[]> = new Map();
  private globalTimeout: number = 5000; // 5 секунд по умолчанию
  private operationCounter: number = 0;

  constructor() {
    super();
  }

  /**
   * Добавить middleware с конфигурацией
   */
  addMiddleware(operation: string, config: MiddlewareConfig): void {
    const middlewares = this.middleware.get(operation) || [];
    
    const internalConfig: InternalMiddlewareConfig = {
      ...config,
      id: generateId(),
      priority: config.priority ?? MiddlewarePriority.NORMAL,
      name: config.name || `middleware_${middlewares.length}`,
      enabled: true,
      stats: {
        totalExecutions: 0,
        averageExecutionTime: 0,
        errorCount: 0,
        abortCount: 0,
        lastExecuted: 0
      }
    };

    middlewares.push(internalConfig);
    
    // Сортируем по приоритету (больше = раньше)
    middlewares.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    
    this.middleware.set(operation, middlewares);
    
    this.emit('middleware:added', {
      operation,
      name: internalConfig.name,
      priority: internalConfig.priority
    });
  }

  /**
   * Удалить middleware по имени
   */
  removeMiddleware(operation: string, name: string): boolean {
    const middlewares = this.middleware.get(operation);
    if (!middlewares) return false;

    const index = middlewares.findIndex(m => m.name === name);
    if (index === -1) return false;

    middlewares.splice(index, 1);
    this.middleware.set(operation, middlewares);
    
    this.emit('middleware:removed', { operation, name });
    return true;
  }

  /**
   * Получить все middleware для операции
   */
  getMiddleware(operation: string): MiddlewareConfig[] {
    const middlewares = this.middleware.get(operation) || [];
    return middlewares.map(({ id, enabled, stats, ...config }) => config);
  }

  /**
   * Очистить все middleware для операции
   */
  clearMiddleware(operation: string): void {
    this.middleware.delete(operation);
    this.emit('middleware:cleared', { operation });
  }

  /**
   * Очистить все middleware для всех операций
   */
  clearAllMiddleware(): void {
    this.middleware.clear();
    this.operationCounter = 0;
    this.emit('middleware:all-cleared');
  }

  /**
   * Выполнить middleware цепочку
   */
  async executeMiddleware(context: MiddlewareContext, operation: () => Promise<any>): Promise<any> {
    const middlewares = this.middleware.get(context.operation) || [];
    const enabledMiddlewares = middlewares.filter(m => 
      m.enabled && (!m.condition || m.condition(context))
    );

    if (enabledMiddlewares.length === 0) {
      return await operation();
    }

    // Дополняем контекст
    const enhancedContext: MiddlewareContext = {
      ...context,
      operationId: context.operationId || `op_${++this.operationCounter}`,
      middlewareStack: [],
      flags: {
        ...context.flags,
        aborted: context.flags?.aborted ?? false,
        modified: context.flags?.modified ?? false,
        inTransaction: context.flags?.inTransaction ?? false
      }
    };

    let index = 0;
    let currentArgs = [...context.args]; // Создаем копию аргументов

    const next = async (modifiedArgs?: any[]): Promise<any> => {
      if (modifiedArgs && modifiedArgs !== currentArgs) {
        currentArgs = [...modifiedArgs]; // Создаем копию модифицированных аргументов
        enhancedContext.flags.modified = true;
        enhancedContext.args = currentArgs; // Обновляем аргументы в контексте
      }

      if (index >= enabledMiddlewares.length) {
        // Создаем новую операцию с модифицированными аргументами
        if (enhancedContext.flags.modified) {
          // Если аргументы были модифицированы, создаем новую операцию
          const originalOperation = operation;
          return await (async () => {
            // Подменяем аргументы в контексте для операции
            const oldArgs = context.args;
            context.args = currentArgs;
            try {
              return await originalOperation();
            } finally {
              context.args = oldArgs; // Восстанавливаем оригинальные аргументы
            }
          })();
        }
        return await operation();
      }

      const middlewareConfig = enabledMiddlewares[index++];
      const startTime = Date.now();
      
      // Обновляем контекст с текущими аргументами
      enhancedContext.args = currentArgs;
      enhancedContext.middlewareStack.push(middlewareConfig.name || 'unknown');

      try {
        const timeout = middlewareConfig.timeout || this.globalTimeout;
        const result = await this.executeWithTimeout(
          middlewareConfig.middleware(enhancedContext, next),
          timeout,
          `Middleware ${middlewareConfig.name} timed out`
        );

        // Обновляем статистику
        this.updateStats(middlewareConfig, Date.now() - startTime, false, false);

        // Обрабатываем результат
        if (this.isMiddlewareResult(result)) {
          if (result.abort) {
            enhancedContext.flags.aborted = true;
            this.updateStats(middlewareConfig, Date.now() - startTime, false, true);
            return result.result;
          }
          
          if (result.modifiedArgs) {
            currentArgs = [...result.modifiedArgs];
            enhancedContext.flags.modified = true;
            enhancedContext.args = currentArgs;
          }
          
          if (result.metadata) {
            Object.assign(enhancedContext.metadata, result.metadata);
          }
          
          return result.result !== undefined ? result.result : await next(currentArgs);
        }

        return result;

      } catch (error) {
        this.updateStats(middlewareConfig, Date.now() - startTime, true, false);
        
        this.emit('middleware:error', {
          operation: context.operation,
          middleware: middlewareConfig.name,
          error,
          context: enhancedContext
        });
        
        throw error;
      }
    };

    return await next();
  }

  /**
   * Получить статистику middleware
   */
  getStats(operation?: string, name?: string): MiddlewareStats | Record<string, MiddlewareStats> {
    if (operation && name) {
      const middlewares = this.middleware.get(operation) || [];
      const middleware = middlewares.find(m => m.name === name);
      return middleware ? { ...middleware.stats } : {
        totalExecutions: 0,
        averageExecutionTime: 0,
        errorCount: 0,
        abortCount: 0,
        lastExecuted: 0
      };
    }

    if (operation) {
      const middlewares = this.middleware.get(operation) || [];
      const stats: Record<string, MiddlewareStats> = {};
      middlewares.forEach(m => {
        stats[m.name || 'unknown'] = { ...m.stats };
      });
      return stats;
    }

    // Возвращаем статистику для всех операций
    const allStats: Record<string, MiddlewareStats> = {};
    for (const [op, middlewares] of this.middleware) {
      middlewares.forEach(m => {
        const key = `${op}.${m.name}`;
        allStats[key] = { ...m.stats };
      });
    }
    return allStats;
  }

  /**
   * Включить/выключить middleware
   */
  toggleMiddleware(operation: string, name: string, enabled: boolean): void {
    const middlewares = this.middleware.get(operation);
    if (!middlewares) return;

    const middleware = middlewares.find(m => m.name === name);
    if (middleware) {
      middleware.enabled = enabled;
      this.emit('middleware:toggled', { operation, name, enabled });
    }
  }

  /**
   * Установить глобальный таймаут для middleware
   */
  setGlobalTimeout(timeout: number): void {
    this.globalTimeout = timeout;
    this.emit('middleware:timeout-changed', { timeout });
  }

  /**
   * Получить все операции с middleware
   */
  getOperations(): string[] {
    return Array.from(this.middleware.keys());
  }

  /**
   * Получить общую статистику
   */
  getGlobalStats(): {
    totalOperations: number;
    totalMiddleware: number;
    totalExecutions: number;
    averageExecutionTime: number;
  } {
    let totalMiddleware = 0;
    let totalExecutions = 0;
    let totalExecutionTime = 0;

    for (const middlewares of this.middleware.values()) {
      totalMiddleware += middlewares.length;
      for (const middleware of middlewares) {
        totalExecutions += middleware.stats.totalExecutions;
        totalExecutionTime += middleware.stats.averageExecutionTime * middleware.stats.totalExecutions;
      }
    }

    return {
      totalOperations: this.middleware.size,
      totalMiddleware,
      totalExecutions,
      averageExecutionTime: totalExecutions > 0 ? totalExecutionTime / totalExecutions : 0
    };
  }

  /**
   * Очистить всю статистику
   */
  clearStats(): void {
    for (const middlewares of this.middleware.values()) {
      for (const middleware of middlewares) {
        middleware.stats = {
          totalExecutions: 0,
          averageExecutionTime: 0,
          errorCount: 0,
          abortCount: 0,
          lastExecuted: 0
        };
      }
    }
    this.emit('middleware:stats-cleared');
  }

  /**
   * Приватные методы
   */

  private async executeWithTimeout<T>(
    promise: Promise<T>,
    timeout: number,
    errorMessage: string
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(errorMessage));
      }, timeout);

      promise
        .then(resolve)
        .catch(reject)
        .finally(() => clearTimeout(timer));
    });
  }

  private isMiddlewareResult(result: any): result is MiddlewareResult {
    return result && typeof result === 'object' && (
      'result' in result ||
      'abort' in result ||
      'modifiedArgs' in result ||
      'metadata' in result
    );
  }

  private updateStats(
    middleware: InternalMiddlewareConfig,
    executionTime: number,
    error: boolean,
    abort: boolean
  ): void {
    const stats = middleware.stats;
    
    stats.totalExecutions++;
    stats.lastExecuted = Date.now();
    
    if (error) {
      stats.errorCount++;
    }
    
    if (abort) {
      stats.abortCount++;
    }
    
    // Обновляем среднее время выполнения
    stats.averageExecutionTime = 
      (stats.averageExecutionTime * (stats.totalExecutions - 1) + executionTime) / stats.totalExecutions;
  }
} 